import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    SxProps,
    Typography,
} from '@mui/material';
import { FC, ReactNode, Ref, useState } from 'react';
import { sxs } from '@utils/mui/sx';

const accordionCardSx: Record<string, SxProps> = {
    details: {
        color: 'text.secondary',
        flexWrap: 'wrap',
        mb: -1.5,
        p: 0,
    },
    summary: {
        '& .MuiAccordionSummary-content': {
            margin: 0,
            pl: 1.5,
        },
        '& .MuiAccordionSummary-expandIconWrapper': {
            ml: -0.5,
        },
        '& .MuiIconButton-edgeEnd': {
            color: 'text.secondary',
            mr: 0.5,
            padding: 0,
        },
        '&&': { minHeight: 'auto' },
        alignItems: 'center',
        flexDirection: 'row-reverse',
        justifyContent: 'flex-start',
        p: 2,
    },
};

const mainSx: (focused: boolean) => SxProps = (focused: boolean) => ({
    '& :hover': {
        backgroundColor: 'grey.600',
    },
    bgcolor: focused ? 'primary.light' : 'background.paper',
    border: focused ? 1 : 'none',
    borderColor: 'primary.main',
    mb: 1,
    position: 'relative',
});

interface AccordionCardSummary {
    count?: number;
    category?: string;
    title: string;
}

interface AccordionCardProps extends AccordionCardSummary {
    children: ReactNode;
    isFocused?: boolean;
    onPremiumClick?: () => void;
    scrollRef?: Ref<HTMLDivElement>;
    sx?: SxProps;
}

const AccordionCardSummary: FC<AccordionCardSummary> = ({ category, count, title }) => {
    const accordionTitle = (
        <Typography component="h4" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
            {title}
            {count && <span data-test-id="analysis-number-for-keyword">&nbsp;({count})</span>}
        </Typography>
    );

    return category ? (
        <Box>
            <Typography sx={{ color: 'grey.300', fontSize: 12 }}>{category}</Typography>
            {accordionTitle}
        </Box>
    ) : (
        accordionTitle
    );
};

const AccordionCard: FC<AccordionCardProps> = ({
    children,
    onPremiumClick,
    isFocused = false,
    scrollRef,
    sx = {},
    ...props
}) => {
    const [expanded, setExpanded] = useState(isFocused);
    const handleAccordionChange = () => {
        if (onPremiumClick) {
            setExpanded(false);
            onPremiumClick();
        } else {
            setExpanded(!expanded);
        }
    };

    return (
        <Card
            ref={scrollRef}
            data-test-id="linked-analysis"
            elevation={0}
            sx={sxs([mainSx(isFocused), sx])}
        >
            <Accordion
                defaultExpanded={isFocused}
                elevation={0}
                expanded={expanded}
                onChange={handleAccordionChange}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionCardSx.summary}>
                    <AccordionCardSummary {...props} />
                </AccordionSummary>
                <AccordionDetails sx={accordionCardSx.details}>{children}</AccordionDetails>
            </Accordion>
        </Card>
    );
};

export default AccordionCard;
